import React, { useCallback, useEffect, useRef, useState } from "react";

import { HelpIcon, SearchIcon } from "./iconography";
import {
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  makeStyles,
  TextField,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { API } from "aws-amplify";
import { CloseIcon } from "./iconography";
import { HelpViewer } from "./HelpViewer/HelpViewer";

import { KB_CHAT_DRAWER_WIDTH_PX } from "../../utils/constants";

const STORAGE_KEYS = {
  isOpen: "KbChatButton:isOpen",
  chatHistory: "KbChatButton:chatHistory",
};

const getLocalStorage = () => {
  if (typeof window === "undefined") return null;
  return window.localStorage;
};

const safeJsonParse = (value, fallback) => {
  if (!value) return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    alignItems: "center",
  },
  drawerPaper: {
    top: "48px",
    height: "calc(100% - 58px)",
    width: `${KB_CHAT_DRAWER_WIDTH_PX}px`,
    borderRadius: "5px",
    margin: "5px",
    display: "flex",
    flexDirection: "column",
  },
  drawerMobile: {
    top: "55px",
    height: "calc(100% - 58px)",
    width: "98%",
    borderRadius: "5px",
    margin: "5px",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    marginLeft: theme.spacing(2),
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerMobile: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    margin: theme.spacing(2),
    width: "100%",
  },
  body: {
    margin: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
  },
  bodyMobile: {
    margin: theme.spacing(2),
    width: "100%",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
  },
  line: {
    marginTop: "0px",
    width: "94%",
    border: "none",
    borderTop: "1px solid #ccc",
    marginLeft: "3%",
  },
  title: {
    marginBottom: "0px",
    marginTop: "0px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  answer: {
    whiteSpace: "pre-wrap",
  },
  chat: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  conversationRoot: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
  },
  chatScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  inputBar: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  bubbleRow: {
    display: "flex",
    width: "100%",
  },
  bubbleRowUser: {
    justifyContent: "flex-end",
  },
  bubbleRowAssistant: {
    justifyContent: "flex-start",
  },
  bubble: {
    maxWidth: "92%",
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 12,
    overflowWrap: "anywhere",
  },
  bubbleUser: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  bubbleAssistant: {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  },
}));

const AIConversation = ({ onSendMessage, handleClose }) => {
  const classes = useStyles();
  const [chatHistory, setChatHistory] = useState(() => {
    const storage = getLocalStorage();
    const parsed = safeJsonParse(
      storage?.getItem(STORAGE_KEYS.chatHistory),
      []
    );
    return Array.isArray(parsed) ? parsed : [];
  });
  const [currentMessage, setCurrentMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const bottomRef = useRef(null);

  const sendMessage = async () => {
    const message = currentMessage.trim();
    if (!message || isLoading) return;

    setIsLoading(true);
    const nextHistory = [...chatHistory, { role: "user", content: message }];
    setChatHistory(nextHistory);
    setCurrentMessage("");

    try {
      const answer = await onSendMessage(message, nextHistory);
      setChatHistory((prev) => [
        ...prev,
        { role: "assistant", content: answer || "" },
      ]);
    } catch (err) {
      setChatHistory((prev) => [
        ...prev,
        {
          role: "assistant",
          content: err?.message || "Failed to query Knowledge Base",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory, isLoading]);

  useEffect(() => {
    const storage = getLocalStorage();
    if (!storage) return;
    storage.setItem(STORAGE_KEYS.chatHistory, JSON.stringify(chatHistory));
  }, [chatHistory]);

  return (
    <div className={classes.conversationRoot}>
      <div className={classes.chatScroll}>
        <div className={classes.chat}>
          {chatHistory.map((m, idx) => {
            const key = `${m.role}-${idx}`;

            if (m.role === "assistant") {
              return (
                <div
                  key={key}
                  className={`${classes.bubbleRow} ${classes.bubbleRowAssistant}`}
                >
                  <div
                    className={`${classes.bubble} ${classes.bubbleAssistant}`}
                  >
                    <HelpViewer
                      handleClose={handleClose}
                      helpPage={{
                        content: m.content,
                        kbFiles: [],
                        kbMedia: [],
                      }}
                    />
                  </div>
                </div>
              );
            }

            return (
              <div
                key={key}
                className={`${classes.bubbleRow} ${classes.bubbleRowUser}`}
              >
                <div className={`${classes.bubble} ${classes.bubbleUser}`}>
                  <Typography variant="body2" className={classes.answer}>
                    {m.content}
                  </Typography>
                </div>
              </div>
            );
          })}

          {isLoading ? (
            <div
              className={`${classes.bubbleRow} ${classes.bubbleRowAssistant}`}
            >
              <div className={`${classes.bubble} ${classes.bubbleAssistant}`}>
                <HelpViewer
                  handleClose={handleClose}
                  helpPage={{ content: "Thinking…", kbFiles: [], kbMedia: [] }}
                />
              </div>
            </div>
          ) : null}

          <div ref={bottomRef} />
        </div>
      </div>

      <div className={classes.inputBar}>
        <div className={classes.form}>
          <TextField
            autoFocus
            variant="outlined"
            placeholder="What would you like to do in the app?"
            value={currentMessage}
            onChange={(e) => setCurrentMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            multiline
            minRows={3}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={sendMessage}
            disabled={isLoading || !currentMessage.trim()}
            startIcon={
              isLoading ? <CircularProgress size={18} /> : <SearchIcon />
            }
          >
            Ask
          </Button>
        </div>
      </div>
    </div>
  );
};

export const KbChatButton = ({ onOpenChange }) => {
  const classes = useStyles();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const [isVisible, setIsVisible] = useState(() => {
    const storage = getLocalStorage();
    return storage?.getItem(STORAGE_KEYS.isOpen) === "true";
  });
  const [isAnimating, setIsAnimating] = useState(false);
  const [conversationKey, setConversationKey] = useState(0);

  const closeDrawer = useCallback(() => {
    setIsAnimating(true);
    setTimeout(() => {
      setIsAnimating(false);
      setIsVisible(false);
    }, 500);

    const storage = getLocalStorage();
    if (storage) {
      storage.setItem(STORAGE_KEYS.isOpen, "false");
      storage.removeItem(STORAGE_KEYS.chatHistory);
    }

    // Ensure the next explicit open starts a fresh conversation.
    setConversationKey((k) => k + 1);
  }, []);

  const openDrawer = useCallback(() => {
    setIsVisible(true);
  }, []);

  const toggleSearchInputVisibility = useCallback(() => {
    if (isVisible) closeDrawer();
    else openDrawer();
  }, [closeDrawer, isVisible, openDrawer]);

  useEffect(() => {
    const storage = getLocalStorage();
    if (!storage) return;
    storage.setItem(STORAGE_KEYS.isOpen, isVisible ? "true" : "false");
  }, [isVisible]);

  useEffect(() => {
    if (typeof onOpenChange === "function") onOpenChange(isVisible);
  }, [isVisible, onOpenChange]);

  useEffect(() => {
    if (!isVisible) return;

    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      closeDrawer();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeDrawer, isVisible]);

  const handleDrawerClose = (event, reason) => {
    void event;
    // Don't close on click-outside, but DO allow ESC to close.
    if (reason === "escapeKeyDown") {
      toggleSearchInputVisibility();
    }
  };

  const sendKbMessage = async (message) => {
    const response = await API.post("transREST", "/sendKbMessage", {
      body: { message },
    });
    return response?.answer || "";
  };

  return (
    <div className={classes.container}>
      <IconButton onClick={toggleSearchInputVisibility}>
        {!isVisible ? <HelpIcon htmlColor="#fff" fontSize="medium" /> : null}
      </IconButton>
      <Drawer
        open={isVisible || isAnimating}
        variant="persistent"
        anchor="right"
        onClose={handleDrawerClose}
        classes={{
          paper: isSmall ? classes.drawerMobile : classes.drawerPaper,
        }}
      >
        <div className={isSmall ? classes.headerMobile : classes.header}>
          <h2 className={classes.title}>Help chat</h2>
          <IconButton aria-label="close" onClick={toggleSearchInputVisibility}>
            <CloseIcon fontSize="large" />
          </IconButton>
        </div>
        <hr className={classes.line} />
        <div className={isSmall ? classes.bodyMobile : classes.body}>
          <AIConversation
            key={conversationKey}
            onSendMessage={sendKbMessage}
            handleClose={toggleSearchInputVisibility}
          />
        </div>
      </Drawer>
    </div>
  );
};
